<template>
  <div class="onboarding-wizard">
    <div class="wizard-hero">
      <div class="wizard-hero__copy">
        <p class="wizard-eyebrow">First steps</p>
        <h1 class="wizard-title">Welcome to IMPROVE</h1>
        <p class="wizard-subtitle">
          Get your trial set up in a few minutes — we’ll walk you through the
          essentials so you’re never starting from a blank screen.
        </p>
      </div>
      <div class="wizard-hero__progress">
        <div class="progress-ring" :style="progressRingStyle">
          <span class="progress-ring__value">{{ completedCount }}/{{ steps.length }}</span>
          <span class="progress-ring__label">done</span>
        </div>
      </div>
    </div>

    <v-row>
      <v-col cols="12" md="4" lg="3">
        <div class="wizard-checklist">
          <h2 class="checklist-title">Your checklist</h2>
          <ul class="checklist-list">
            <li
              v-for="(step, index) in steps"
              :key="step.id"
              class="checklist-item"
              :class="{
                'is-active': currentStep === index,
                'is-done': step.done,
                'is-waiting': loading
              }"
              @click="goToStep(index)"
            >
              <span class="checklist-item__index">
                <v-icon v-if="step.done" small>fa-check</v-icon>
                <template v-else>{{ index + 1 }}</template>
              </span>
              <span class="checklist-item__body">
                <span class="checklist-item__label">{{ step.title }}</span>
                <span class="checklist-item__hint">{{ step.short }}</span>
              </span>
            </li>
          </ul>

          <div class="checklist-cta">
            <v-btn
              block
              color="primary"
              class="mb-2"
              depressed
              @click="goToBuyNow"
            >
              Buy Now
            </v-btn>
            <v-btn
              block
              outlined
              color="directHeader"
              :loading="saving"
              :disabled="loading"
              @click="skipWizard"
            >
              Skip for now
            </v-btn>
            <p v-if="showTourLinks" class="wizard-tour-links">
              <button type="button" class="wizard-text-link" @click="$emit('start-tour')">
                Take the two-minute tour
              </button>
              <span class="wizard-tour-links__sep">·</span>
              <button type="button" class="wizard-text-link" @click="$emit('join-live')">
                Join IMPROVE Live
              </button>
            </p>
          </div>
        </div>
      </v-col>

      <v-col cols="12" md="8" lg="9">
        <div class="wizard-panel">
          <div class="wizard-panel__header">
            <span class="step-badge">Step {{ currentStep + 1 }} of {{ steps.length }}</span>
            <h2 class="wizard-panel__title">{{ activeStep.title }}</h2>
            <p class="wizard-panel__desc">{{ activeStep.description }}</p>
          </div>

          <div v-if="loading" class="wizard-step text-center py-10">
            <v-progress-circular indeterminate color="primary" />
            <p class="mock-hint mt-4">Loading your setup…</p>
          </div>

          <!-- Step 0: Welcome -->
          <div v-else-if="currentStep === 0" class="wizard-step">
            <div class="feature-grid">
              <div
                v-for="item in welcomeHighlights"
                :key="item.title"
                class="feature-card"
              >
                <div class="feature-card__icon" :style="{ background: item.tint }">
                  <v-icon>{{ item.icon }}</v-icon>
                </div>
                <h3>{{ item.title }}</h3>
                <p>{{ item.text }}</p>
              </div>
            </div>
          </div>

          <!-- Step 1: Add users -->
          <div v-else-if="currentStep === 1" class="wizard-step">
            <div class="mock-form">
              <v-row dense>
                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model="newUser.first_name"
                    label="First name"
                    outlined
                    dense
                    hide-details
                    :disabled="creatingUser"
                  />
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model="newUser.last_name"
                    label="Last name"
                    outlined
                    dense
                    hide-details
                    :disabled="creatingUser"
                  />
                </v-col>
                <v-col cols="12">
                  <v-text-field
                    v-model="newUser.email"
                    label="Email address"
                    outlined
                    dense
                    hide-details
                    :disabled="creatingUser"
                  />
                </v-col>
              </v-row>
              <p class="mock-hint">
                Adds a real user to your company and sends them a welcome email
                with a set-password link, so they can log in straight away.
                <template v-if="maxUsers">
                  Your account allows <strong>{{ maxUsers }} users</strong>
                  ({{ userCount }} so far).
                </template>
              </p>
              <p v-if="atUserLimit" class="mock-hint mock-hint--warn">
                <template v-if="isTrial">
                  Your free trial includes {{ maxUsers }} people and they are all
                  added. Buy IMPROVE to add more.
                </template>
                <template v-else>
                  Your account is at its user limit, so adding more is disabled
                  here. Account Status shows your plan.
                </template>
              </p>
              <div class="mock-users" v-if="teamUsers.length">
                <p class="mock-users__label">Existing team — click to fill the form</p>
                <div class="mock-users__scroll">
                  <div
                    v-for="user in teamUsers"
                    :key="user.id"
                    class="mock-user-row"
                    :class="{ 'is-selected': selectedTeamUserId === user.id }"
                    @click="fillUserForm(user)"
                  >
                    <v-avatar size="32" color="primary" class="white--text">
                      {{ user.initials }}
                    </v-avatar>
                    <div>
                      <strong>{{ user.name }}</strong>
                      <span>{{ user.email }}</span>
                    </div>
                  </div>
                </div>
              </div>
              <v-btn
                color="primary"
                class="mt-4"
                depressed
                :loading="creatingUser"
                :disabled="!canCreateUser || atUserLimit"
                @click="createTeamUser"
              >
                <v-icon left small>fa-user-plus</v-icon>
                Add team member
              </v-btn>
            </div>
          </div>

          <!-- Step 2: Explore courses -->
          <div v-else-if="currentStep === 2" class="wizard-step">
            <div v-if="!starterCourses.length" class="mock-hint courses-installing">
              <v-progress-circular
                v-if="coursesPending"
                indeterminate
                size="18"
                width="2"
                color="primary"
                class="mr-2"
              />
              <span v-if="coursesPending">
                Your course library is being set up — this takes about a minute
                on a new account. The courses will appear here by themselves.
              </span>
              <span v-else>
                No courses found in your library yet. You can continue and
                assign later from the Course Library.
              </span>
            </div>
            <template v-else>
              <!-- The WHOLE library (Muhammad, 18 Sep 2026: "show full course
                   library and multiple course selection", "show 6 courses
                   like in the screenshot and add a scroll"): six cards in
                   view, the rest scroll, a search box to get about, and any
                   number of courses ticked for the assign step. -->
              <div class="course-toolbar">
                <v-text-field
                  v-model="courseSearch"
                  placeholder="Search your library"
                  prepend-inner-icon="fa-search"
                  outlined
                  dense
                  hide-details
                  clearable
                  class="course-search"
                />
                <span class="course-toolbar__count">
                  {{ selectedCourses.length }} selected
                  <button
                    v-if="selectedCourses.length"
                    type="button"
                    class="wizard-text-link ml-2"
                    @click="clearCourses"
                  >
                    Clear
                  </button>
                </span>
              </div>
              <div class="course-scroll">
                <div class="course-grid">
                  <div
                    v-for="course in filteredCourses"
                    :key="course.id"
                    class="course-card"
                    :class="{ 'is-selected': isCourseSelected(course.id) }"
                    @click="toggleCourse(course)"
                  >
                    <div class="course-card__badge">{{ course.tag }}</div>
                    <h3>{{ course.name }}</h3>
                    <p>{{ course.meta }}</p>
                    <v-icon
                      v-if="isCourseSelected(course.id)"
                      class="course-card__check"
                      color="primary"
                    >
                      fa-check-circle
                    </v-icon>
                  </div>
                </div>
                <p v-if="!filteredCourses.length" class="mock-hint">
                  No course matches “{{ courseSearch }}”.
                </p>
              </div>
              <p class="mock-hint mt-4">
                Tick every course you want to assign in the next step.
                Showing {{ filteredCourses.length }} of {{ starterCourses.length }} courses in your library.
              </p>
            </template>
          </div>

          <!-- Step 3: Assign courses -->
          <div v-else-if="currentStep === 3" class="wizard-step">
            <div class="assign-panel">
              <div class="assign-panel__left">
                <h3>Assign to</h3>
                <!-- v-model + multiple: the group's own active state IS the
                     selection. With :input-value + @click the group ignored the
                     bound value and toggled its own, so the pre-selected admin
                     had no tick and each click showed the opposite of what
                     would be enrolled (17 Sep 2026). -->
                <v-chip-group v-model="assignedUserIds" column multiple>
                  <v-chip
                    v-for="person in assignTargets"
                    :key="person.id"
                    :value="person.id"
                    filter
                    outlined
                  >
                    {{ person.name }}
                  </v-chip>
                </v-chip-group>
                <p v-if="!assignTargets.length" class="mock-hint">
                  Add a team member first, or assign to yourself.
                </p>
              </div>
              <div class="assign-panel__right">
                <h3>{{ selectedCourses.length === 1 ? 'Course' : 'Courses' }}</h3>
                <div class="assign-course-preview">
                  <v-icon large color="primary">fa-graduation-cap</v-icon>
                  <div v-if="selectedCourses.length">
                    <ul class="assign-course-list">
                      <li v-for="c in selectedCourses" :key="c.id">{{ c.name }}</li>
                    </ul>
                    <span>{{ selectedCourses.length === 1 ? 'Will be enrolled via IMPROVE' : selectedCourses.length + ' courses, enrolled via IMPROVE' }}</span>
                  </div>
                  <div v-else>
                    <strong>{{ coursesPending ? 'Your courses are being set up…' : 'Pick a course first' }}</strong>
                    <span v-if="coursesPending">They will appear here in about a minute</span>
                    <span v-else>Choose some in Explore starter courses</span>
                  </div>
                </div>
                <v-btn
                  color="primary"
                  class="mt-4"
                  depressed
                  :loading="assigning"
                  :disabled="!canAssign"
                  @click="assignTraining"
                >
                  {{ selectedCourses.length > 1 ? 'Assign ' + selectedCourses.length + ' courses' : 'Assign course' }}
                </v-btn>
              </div>
            </div>
            <p class="mock-hint mt-4">
              This enrols the selected people onto {{ selectedCourses.length > 1 ? 'these courses' : 'the course' }} for real.
            </p>
          </div>

          <!-- Step 4: Finish -->
          <div v-else class="wizard-step wizard-step--finish">
            <div class="finish-card">
              <div class="finish-card__icon">
                <v-icon large>fa-rocket</v-icon>
              </div>
              <h3>You’re ready to explore</h3>
              <p>
                Your first steps are complete. Jump into the platform, or
                subscribe when you’re ready to go live.
              </p>
              <div class="finish-actions">
                <v-btn color="primary" large depressed :loading="saving" @click="finishWizard">
                  Go to dashboard
                </v-btn>
                <v-btn text color="primary" @click="goToBuyNow">
                  Buy Now
                </v-btn>
              </div>
            </div>
          </div>

          <div class="wizard-panel__footer">
            <v-btn
              text
              color="grey700"
              :disabled="currentStep === 0 || saving || loading"
              @click="prevStep"
            >
              Back
            </v-btn>
            <div class="footer-right">
              <v-btn
                v-if="!activeStep.done && currentStep < steps.length - 1"
                text
                color="primary"
                class="mr-2"
                :loading="saving"
                :disabled="loading"
                @click="markStepDone"
              >
                Mark as done
              </v-btn>
              <v-btn
                v-if="currentStep < steps.length - 1"
                color="primary"
                depressed
                :loading="saving"
                :disabled="loading"
                @click="nextStep"
              >
                Continue
                <v-icon right small>fa-arrow-right</v-icon>
              </v-btn>
              <v-btn
                v-else
                color="primary"
                depressed
                :loading="saving"
                :disabled="loading"
                @click="finishWizard"
              >
                Finish
              </v-btn>
            </div>
          </div>
        </div>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import userAPI from "@/api/user";
import courseAPI from "@/api/course";
import companyAPI from "@/api/company";
import {
  getOnboardingStorageKey,
  markOnboardingCompleted,
  getCachedOnboarding,
  mergeOnboardingPayload,
  isTrialCompanyAdmin
} from "@/utils/onboarding";

// How long the first-run screen waits for its three bootstrap calls before it
// renders anyway. Far above their real cost (0.09s to 0.38s each, measured on
// staging) and far below the 300 SECOND global axios default that would
// otherwise be the only thing bounding the spinner.
const BOOTSTRAP_DEADLINE_MS = 15000;

// Laravel validation failures arrive as { message: "The given data was
// invalid.", errors: { email: ["The email has already been taken."] } }.
// The person needs the second line, not the first.
function serverMessage(err, fallback) {
  const data = err && err.response && err.response.data;
  if (data && data.errors && typeof data.errors === "object") {
    for (const key of Object.keys(data.errors)) {
      const v = data.errors[key];
      const first = Array.isArray(v) ? v[0] : v;
      if (typeof first === "string" && first.trim()) return first;
    }
  }
  if (data && typeof data.message === "string" && data.message.trim()) return data.message;
  return fallback;
}


const DEFAULT_STEPS = [
  {
    id: "welcome",
    title: "Welcome aboard",
    short: "See what’s included",
    description:
      "Here’s what you’ll set up during your free trial — users, courses, and your first assignment.",
    done: false
  },
  {
    id: "users",
    title: "Add your team",
    short: "Invite your colleagues",
    description:
      "Add colleagues so they can start learning.",
    done: false
  },
  {
    id: "courses",
    title: "Explore starter courses",
    short: "Browse your library",
    description:
      "Pick a course from your library to use for your first assignment.",
    done: false
  },
  {
    id: "assign",
    title: "Assign courses",
    short: "Give people courses",
    description:
      "Enrol people onto courses — this is how learning gets underway.",
    done: false
  },
  {
    id: "finish",
    title: "You’re all set",
    short: "Next actions",
    description:
      "That’s the guided setup. Explore the rest of IMPROVE, or upgrade when you’re ready.",
    done: false
  }
];

export default {
  name: "OnboardingWizard",
  data() {
    return {
      loading: true,
      saving: false,
      creatingUser: false,
      assigning: false,
      currentStep: 0,
      steps: JSON.parse(JSON.stringify(DEFAULT_STEPS)),
      newUser: {
        first_name: "",
        last_name: "",
        email: ""
      },
      teamUsers: [],
      selectedTeamUserId: null,
      // The whole library (the name predates the full list).
      starterCourses: [],
      courseSearch: "",
      // True while the library is still empty and we are polling for it.
      coursesPending: false,
      coursesRetryTimer: null,
      coursesRetries: 0,
      // [{ id, name }], any number. selectedCourseId/Name are derived from
      // the first entry for the older payload shape.
      selectedCourses: [],
      courseSaveTimer: null,
      assignedUserIds: [],
      welcomeHighlights: [
        {
          title: "Starter courses",
          text: "A curated set so your trial isn’t empty on day one.",
          icon: "fa-book",
          tint: "rgba(8, 160, 168, 0.2)"
        },
        {
          title: "Your team",
          text: "Invite your colleagues so they can start learning straight away.",
          icon: "fa-users",
          tint: "rgba(8, 160, 168, 0.12)"
        },
        {
          title: "Go live anytime",
          text: "Buy Now when you’re ready — no need to wait for us.",
          icon: "fa-credit-card",
          tint: "rgba(0, 36, 88, 0.1)"
        }
      ]
    };
  },
  computed: {
    activeStep() {
      return this.steps[this.currentStep] || this.steps[0];
    },
    completedCount() {
      return this.steps.filter(s => s.done).length;
    },
    progressPercent() {
      return Math.round((this.completedCount / this.steps.length) * 100);
    },
    progressRingStyle() {
      return {
        background: `conic-gradient(var(--v-primary-base) ${this.progressPercent}%, #e8eef2 0)`
      };
    },
    maxUsers() {
      const n = Number(this.$store.getters.maxUsers);
      return n > 0 ? n : 0;
    },
    userCount() {
      // The admin plus everyone on the team list.
      return this.teamUsers.length + 1;
    },
    atUserLimit() {
      return !!this.maxUsers && this.userCount >= this.maxUsers;
    },
    // A trial is capped at five people however it is banded, so the message
    // says why. Same test the rest of the wizard uses.
    isTrial() {
      return isTrialCompanyAdmin(this.$store.getters);
    },
    canCreateUser() {
      return (
        this.newUser.first_name.trim().length >= 2 &&
        this.newUser.last_name.trim().length >= 2 &&
        this.newUser.email.trim().includes("@") &&
        !this.creatingUser
      );
    },
    currentUser() {
      return this.$store.getters.user || {};
    },
    assignTargets() {
      const me = this.currentUser;
      const self = me && me.id
        ? [{
            id: me.id,
            name: `${me.first_name || ""} ${me.last_name || ""}`.trim() || "You (Admin)"
          }]
        : [];
      const others = this.teamUsers.map(u => ({ id: u.id, name: u.name }));
      const seen = new Set();
      return [...self, ...others].filter(p => {
        if (seen.has(p.id)) return false;
        seen.add(p.id);
        return true;
      });
    },
    canAssign() {
      return (
        this.selectedCourses.length > 0 &&
        this.assignedUserIds.length > 0 &&
        !this.assigning
      );
    },
    selectedCourseId() {
      return this.selectedCourses.length ? this.selectedCourses[0].id : null;
    },
    selectedCourseName() {
      return this.selectedCourses.length ? this.selectedCourses[0].name : "";
    },
    filteredCourses() {
      const q = (this.courseSearch || "").trim().toLowerCase();
      if (!q) return this.starterCourses;
      return this.starterCourses.filter(c => (c.name || "").toLowerCase().indexOf(q) !== -1);
    },
    showTourLinks() {
      return isTrialCompanyAdmin(this.$store.getters);
    }
  },
  mounted() {
    this.bootstrap();
  },
  beforeDestroy() {
    this.stopCoursePolling();
    if (this.courseSaveTimer) {
      clearTimeout(this.courseSaveTimer);
      this.courseSaveTimer = null;
      this.persistProgress();
    }
  },
  methods: {
    async bootstrap() {
      this.loading = true;
      try {
        // A deadline, not another catch. Each loader below already catches its
        // own failure and this method has a finally, so a REJECTION can never
        // strand the spinner. What can is a socket that stalls without ever
        // erroring - sleep/wake, a VPN drop, a proxy holding the connection -
        // because the only live axios timeout is a global 300s default. That
        // is five minutes of "Loading your setup..." on the first screen a
        // trial customer ever sees.
        //
        // Whatever has landed by the deadline is enough to render: step 0 is
        // static, and the team and course lists degrade to empty exactly as
        // they already do when a request fails outright. Anything still in
        // flight keeps going and fills the screen in when it arrives.
        await Promise.race([
          Promise.all([
            this.loadProgress(),
            this.loadTeamUsers(),
            this.loadCourses()
          ]),
          new Promise(resolve => setTimeout(resolve, BOOTSTRAP_DEADLINE_MS))
        ]);
        if (this.currentUser && this.currentUser.id) {
          if (!this.assignedUserIds.includes(this.currentUser.id)) {
            this.assignedUserIds = [this.currentUser.id];
          }
        }
      } finally {
        this.loading = false;
      }
    },
    async loadProgress() {
      try {
        const resp = await companyAPI.getOnboardingProgress();
        const data = (resp.data && resp.data.data) ? resp.data.data : {};
        // mergeOnboardingPayload, never a bare cache write: it merges the
        // guided-tour chapter state per chapter, where a wholesale
        // cacheOnboardingLocally(buildPayload(...)) would silently erase it
        // (buildPayload knows nothing about tourChapters).
        mergeOnboardingPayload(data);
        this.applyProgress(data);
        mergeOnboardingPayload(
          this.buildPayload({
            completed:
              !!(data && data.completed) || !!getCachedOnboarding().completed
          })
        );
      } catch (e) {
        this.restoreLocalProgress();
      }
    },
    applyProgress(data) {
      if (!data || typeof data !== "object") return;
      if (Array.isArray(data.steps) && data.steps.length) {
        this.steps = this.steps.map(step => {
          const saved = data.steps.find(s => s.id === step.id);
          return { ...step, done: !!(saved && saved.done) };
        });
      }
      if (typeof data.currentStep === "number") {
        this.currentStep = Math.min(
          Math.max(data.currentStep, 0),
          this.steps.length - 1
        );
      }
      if (Array.isArray(data.selectedCourses) && data.selectedCourses.length) {
        this.selectedCourses = data.selectedCourses
          .filter(c => c && c.id)
          .map(c => ({ id: c.id, name: c.name || "" }));
      } else if (data.selectedCourseId) {
        this.selectedCourses = [{ id: data.selectedCourseId, name: data.selectedCourseName || "" }];
      }
      if (data.completed) markOnboardingCompleted();
    },
    restoreLocalProgress() {
      try {
        const raw = localStorage.getItem(getOnboardingStorageKey());
        if (!raw) return;
        this.applyProgress(JSON.parse(raw));
      } catch (e) {
        // ignore
      }
    },
    buildPayload(extra = {}) {
      return {
        completed: !!extra.completed,
        currentStep: this.currentStep,
        steps: this.steps.map(s => ({ id: s.id, done: !!s.done })),
        selectedCourseId: this.selectedCourseId,
        selectedCourseName: this.selectedCourseName,
        selectedCourses: this.selectedCourses.map(c => ({ id: c.id, name: c.name })),
        ...extra
      };
    },
    async persistProgress(extra = {}) {
      // Never save before the progress has loaded: this component sends its
      // WHOLE state, so a save from the defaults (every step false, no
      // course) would replace what was stored. The server latches ticks and
      // completion since 17 Sep 2026 as well; this keeps a stale save from
      // ever leaving the browser. The controls are disabled while loading,
      // this is the belt to their braces.
      if (this.loading) return;
      // Route through mergeOnboardingPayload (which also unions the tour
      // flags and per-chapter tour state from the cache) so a wizard save
      // can never regress or erase tour progress.
      const payload = mergeOnboardingPayload(this.buildPayload(extra));
      this.saving = true;
      try {
        await companyAPI.updateOnboardingProgress(payload);
      } catch (e) {
        // Keep local cache even if server save fails (e.g. migration not run yet)
      } finally {
        this.saving = false;
      }
    },
    async loadTeamUsers() {
      try {
        const resp = await userAPI.getClients("?limit=50&page=1", "");
        const list = (resp.data && resp.data.data) ? resp.data.data : [];
        const meId = this.currentUser && this.currentUser.id;
        this.teamUsers = list
          .filter(u => u.id !== meId)
          .map(u => ({
            id: u.id,
            first_name: u.first_name || "",
            last_name: u.last_name || "",
            name: `${u.first_name || ""} ${u.last_name || ""}`.trim() || u.email,
            email: u.email,
            initials: `${(u.first_name || "?").charAt(0)}${(u.last_name || "?").charAt(0)}`.toUpperCase()
          }));
      } catch (e) {
        this.teamUsers = [];
      }
    },
    async loadCourses() {
      try {
        // getCoursesData (/courses-data), NOT getCourses (/courses): the old
        // endpoint runs ~10 aggregate accessors per course over course_users
        // plus a default "enrolled" include, and measured 40 SECONDS for
        // limit=8 on staging - it was the entire "Loading your setup..."
        // hang. courses-data serves the real course screens in ~0.2s and
        // carries everything this picker needs.
        // The whole library in one page: courses-data returns a trial's 133
        // courses in ~0.6s on staging (the old /courses took 40s for eight).
        const resp = await courseAPI.getCoursesData("?limit=500&page=1", "");
        const list = (resp.data && resp.data.data) ? resp.data.data : [];
        this.starterCourses = list.map((c, index) => ({
          id: c.id,
          name: c.name,
          meta: c.duration ? `${c.duration} · Course` : "Course",
          tag: index === 0 ? "Recommended" : "Library"
        }));
        if (!this.selectedCourses.length && this.starterCourses.length) {
          this.selectedCourses = [{ id: this.starterCourses[0].id, name: this.starterCourses[0].name }];
        } else if (this.selectedCourses.length) {
          // Names as the library shows them now.
          this.selectedCourses = this.selectedCourses.map(sel => {
            const match = this.starterCourses.find(c => c.id === sel.id);
            return match ? { id: sel.id, name: match.name } : sel;
          });
        }
      } catch (e) {
        this.starterCourses = [];
      }
      // A brand-new account's library is copied in by a queued job after
      // signup (133 courses, about a minute on staging). Someone who skips
      // the tour reaches this step before it lands, and this list was only
      // ever loaded once, so step 3 stayed empty and step 4 said "Pick a
      // course first" with Assign disabled until a full page refresh (found
      // 17 Sep 2026). Keep looking for ten minutes (a busy queue can hold the
      // copy job behind others: four suites signing up at once pushed it past
      // three minutes on staging); stop as soon as it
      // arrives or the wizard is left.
      if (!this.starterCourses.length && this.coursesRetries < 120) {
        this.coursesPending = true;
        this.coursesRetries += 1;
        this.coursesRetryTimer = setTimeout(() => {
          this.coursesRetryTimer = null;
          this.loadCourses();
        }, 5000);
      } else {
        this.coursesPending = false;
      }
    },
    stopCoursePolling() {
      if (this.coursesRetryTimer) {
        clearTimeout(this.coursesRetryTimer);
        this.coursesRetryTimer = null;
      }
      this.coursesPending = false;
    },
    isCourseSelected(id) {
      return this.selectedCourses.some(c => c.id === id);
    },
    toggleCourse(course) {
      const idx = this.selectedCourses.findIndex(c => c.id === course.id);
      if (idx === -1) this.selectedCourses.push({ id: course.id, name: course.name });
      else this.selectedCourses.splice(idx, 1);
      this.$set(this.steps[2], "done", true);
      this.queueCourseSave();
    },
    clearCourses() {
      this.selectedCourses = [];
      this.queueCourseSave();
    },
    // One save per burst of ticks, not one per tick.
    queueCourseSave() {
      if (this.courseSaveTimer) clearTimeout(this.courseSaveTimer);
      this.courseSaveTimer = setTimeout(() => {
        this.courseSaveTimer = null;
        this.persistProgress();
      }, 600);
    },
    goToStep(index) {
      if (this.loading) return;
      this.currentStep = index;
      this.persistProgress();
    },
    prevStep() {
      if (this.currentStep > 0) {
        this.currentStep -= 1;
        this.persistProgress();
      }
    },
    async nextStep() {
      await this.markStepDone();
      if (this.currentStep < this.steps.length - 1) {
        this.currentStep += 1;
        await this.persistProgress();
      }
    },
    async markStepDone() {
      this.$set(this.steps[this.currentStep], "done", true);
      await this.persistProgress();
    },
    fillUserForm(user) {
      this.selectedTeamUserId = user.id;
      this.newUser = {
        first_name: user.first_name || "",
        last_name: user.last_name || "",
        email: user.email || ""
      };
    },
    async createTeamUser() {
      if (!this.canCreateUser) return;
      this.creatingUser = true;
      try {
        await userAPI.createUser({
          first_name: this.newUser.first_name.trim(),
          last_name: this.newUser.last_name.trim(),
          email: this.newUser.email.trim(),
          groups: [],
          // Send the welcome email. Without it the person added here has no
          // way to set a password and can never log in, which defeats the
          // point of the step (add a person, assign a course, see the proof).
          // The tour launcher promises the email; this makes it true.
          send_mail: true
        });
        this.$snotify.success("Team member added.");
        this.newUser = { first_name: "", last_name: "", email: "" };
        this.selectedTeamUserId = null;
        await this.loadTeamUsers();
        this.$set(this.steps[1], "done", true);
        await this.persistProgress();
      } catch (err) {
        this.$snotify.error(
          serverMessage(err, "Could not add user. Check the details and try again.")
        );
      } finally {
        this.creatingUser = false;
      }
    },
    async assignTraining() {
      if (!this.canAssign) return;
      this.assigning = true;
      try {
        // ⚠️ INVERTED CONTRACT - do not "fix" the field names. On Direct's
        // POST /course/enrol/{id} the branches are swapped server-side:
        // `unassign_users` runs the ADD sub-action and `assign_users` runs
        // the REMOVE one. The live enrol panel (EnrolUsers.vue) compensates
        // the same way - its assign_users line is commented out and the
        // users to enrol go in unassign_users. Written honestly, this call
        // returns 200, shows a success toast, and enrols NOBODY (verified
        // against staging: assign_users wrote 0 rows, unassign_users wrote
        // the enrolment). Same load-bearing-name situation as the n8n wire
        // contract's user_mssg: both sides lie consistently; changing one
        // side alone breaks real enrolment flows.
        // One enrol call per course, in order, so a failure names the
        // course and the ones before it are already done.
        for (const course of this.selectedCourses) {
          try {
            await courseAPI.enrolUsers(course.id, {
              assign_users: [],
              unassign_users: this.assignedUserIds,
              assign_groups: [],
              unassign_groups: [],
              selectedDate: "",
              enrolAll: false
            });
          } catch (err) {
            err.courseName = course.name;
            throw err;
          }
        }
        this.$snotify.success(
          this.selectedCourses.length > 1
            ? this.selectedCourses.length + " courses assigned successfully."
            : "Course assigned successfully."
        );
        this.$set(this.steps[3], "done", true);
        await this.persistProgress();
      } catch (err) {
        const which = err && err.courseName ? " (" + err.courseName + ")" : "";
        this.$snotify.error(
          serverMessage(err, "Could not assign the course" + which + ". Try again from Course Library if needed.")
        );
      } finally {
        this.assigning = false;
      }
    },
    async finishWizard() {
      this.$set(this.steps[this.steps.length - 1], "done", true);
      markOnboardingCompleted();
      await this.persistProgress({ completed: true });
      this.goToDashboard();
    },
    async skipWizard() {
      if (this.loading) return;
      markOnboardingCompleted();
      await this.persistProgress({ completed: true });
      this.$router.push("/admin/users");
    },
    goToDashboard() {
      const level = this.$store.getters.level;
      if (level === 2) this.$router.push("/admin/users");
      else this.$router.push("/");
    },
    goToBuyNow() {
      this.$router.push("/billing");
    }
  }
};
</script>

<style scoped>
.mock-hint--warn {
  color: #b5541c;
}

.checklist-item.is-waiting {
  pointer-events: none;
  opacity: 0.7;
}

.courses-installing {
  display: flex;
  align-items: center;
}

.onboarding-wizard {
  max-width: 1180px;
  margin: 0 auto;
  padding: 8px 8px 32px;
  text-align: left;
}

.wizard-hero {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  margin-bottom: 24px;
  padding: 28px 32px;
  border-radius: 16px;
  background:
    linear-gradient(135deg, rgba(123, 200, 163, 0.18), rgba(22, 43, 68, 0.06)),
    #fff;
  border: 1px solid rgba(22, 43, 68, 0.08);
}

.wizard-eyebrow {
  margin: 0 0 6px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #08a0a8;
}

.wizard-title {
  margin: 0 0 8px;
  font-size: 32px;
  font-weight: 700;
  color: #002458;
  line-height: 1.15;
}

.wizard-subtitle {
  margin: 0;
  max-width: 560px;
  font-size: 16px;
  line-height: 1.5;
  color: #5a6a7a;
}

.progress-ring {
  width: 88px;
  height: 88px;
  border-radius: 50%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: relative;
}

.progress-ring::before {
  content: "";
  position: absolute;
  inset: 8px;
  border-radius: 50%;
  background: #fff;
}

.progress-ring__value,
.progress-ring__label {
  position: relative;
  z-index: 1;
}

.progress-ring__value {
  font-size: 18px;
  font-weight: 700;
  color: #002458;
  line-height: 1;
}

.progress-ring__label {
  font-size: 11px;
  color: #7a8a9a;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.wizard-checklist {
  background: #fff;
  border: 1px solid rgba(22, 43, 68, 0.08);
  border-radius: 16px;
  padding: 20px 16px;
  height: 100%;
}

.checklist-title {
  margin: 0 0 16px;
  font-size: 14px;
  font-weight: 700;
  color: #002458;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.checklist-list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.checklist-item {
  display: flex;
  gap: 12px;
  align-items: flex-start;
  padding: 12px 10px;
  border-radius: 10px;
  cursor: pointer;
  transition: background 0.15s ease;
  margin-bottom: 4px;
}

.checklist-item:hover {
  background: #f4f8f6;
}

.checklist-item.is-active {
  background: rgba(123, 200, 163, 0.16);
}

.checklist-item__index {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: #eef2f5;
  color: #5a6a7a;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
  flex-shrink: 0;
}

.checklist-item.is-done .checklist-item__index {
  background: #08a0a8;
  color: #fff;
}

.checklist-item.is-active:not(.is-done) .checklist-item__index {
  background: #002458;
  color: #fff;
}

.checklist-item__label {
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: #002458;
}

.checklist-item__hint {
  display: block;
  font-size: 12px;
  color: #7a8a9a;
  margin-top: 2px;
}

.checklist-cta {
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid #eef2f5;
}

.wizard-tour-links {
  margin: 14px 0 0;
  text-align: center;
  font-size: 13px;
  color: #7a8a9a;
}

.wizard-tour-links__sep {
  margin: 0 6px;
  color: #c0c8d0;
}

.wizard-text-link {
  background: none;
  border: 0;
  padding: 0;
  color: #08a0a8;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}

.wizard-text-link:hover {
  text-decoration: underline;
}

.wizard-panel {
  background: #fff;
  border: 1px solid rgba(22, 43, 68, 0.08);
  border-radius: 16px;
  min-height: 480px;
  display: flex;
  flex-direction: column;
}

.wizard-panel__header {
  padding: 28px 32px 8px;
}

.step-badge {
  display: inline-block;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #08a0a8;
  margin-bottom: 8px;
}

.wizard-panel__title {
  margin: 0 0 8px;
  font-size: 24px;
  font-weight: 700;
  color: #002458;
}

.wizard-panel__desc {
  margin: 0;
  color: #5a6a7a;
  font-size: 15px;
  line-height: 1.5;
  max-width: 640px;
}

.wizard-step {
  flex: 1;
  padding: 20px 32px 8px;
}

.wizard-panel__footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 32px 24px;
  margin-top: auto;
  border-top: 1px solid #eef2f5;
}

.footer-right {
  display: flex;
  align-items: center;
}

.feature-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
}

.feature-card {
  border: 1px solid #eef2f5;
  border-radius: 12px;
  padding: 20px;
  background: #fafcfb;
}

.feature-card__icon {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 14px;
  color: #002458;
}

.feature-card h3 {
  margin: 0 0 6px;
  font-size: 16px;
  color: #002458;
}

.feature-card p {
  margin: 0;
  font-size: 13px;
  line-height: 1.45;
  color: #5a6a7a;
}

.mock-form {
  max-width: 520px;
}

.mock-hint {
  margin: 16px 0 0;
  font-size: 13px;
  color: #7a8a9a;
  line-height: 1.45;
}

.mock-users {
  margin-top: 16px;
}

.mock-users__label {
  margin: 0 0 8px;
  font-size: 12px;
  font-weight: 600;
  color: #5a6a7a;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.mock-users__scroll {
  max-height: 220px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-right: 4px;
  border: 1px solid #e8eef2;
  border-radius: 12px;
  background: #fafcfb;
  padding: 8px;
}

.mock-user-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border-radius: 10px;
  background: #fff;
  border: 1px solid transparent;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease;
  flex-shrink: 0;
}

.mock-user-row:hover {
  background: #f4f8f6;
  border-color: #d7e6de;
}

.mock-user-row.is-selected {
  background: rgba(123, 200, 163, 0.16);
  border-color: #08a0a8;
}

.mock-user-row strong {
  display: block;
  font-size: 14px;
  color: #002458;
}

.mock-user-row span {
  font-size: 12px;
  color: #7a8a9a;
}

.course-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 12px;
}

.course-search {
  max-width: 360px;
}

.course-toolbar__count {
  font-size: 13px;
  color: #42505e;
  white-space: nowrap;
}

/* Six cards in view (three rows of two), the rest scroll. */
.course-scroll {
  max-height: 456px;
  overflow-y: auto;
  padding: 4px 6px 4px 4px;
  margin: 0 -4px;
  border-radius: 12px;
}

.course-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 14px;
}

.assign-course-list {
  margin: 0 0 4px;
  padding: 0 0 0 18px;
  color: #002458;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.45;
}

.course-card {
  position: relative;
  border: 1px solid #e8eef2;
  border-radius: 12px;
  padding: 18px;
  cursor: pointer;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
  background: #fff;
}

.course-card:hover,
.course-card.is-selected {
  border-color: #08a0a8;
  box-shadow: 0 0 0 3px rgba(8, 160, 168, 0.2);
}

.course-card__badge {
  display: inline-block;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #002458;
  background: rgba(123, 200, 163, 0.25);
  padding: 3px 8px;
  border-radius: 999px;
  margin-bottom: 10px;
}

.course-card h3 {
  margin: 0 0 4px;
  font-size: 15px;
  color: #002458;
}

.course-card p {
  margin: 0;
  font-size: 12px;
  color: #7a8a9a;
}

.course-card__check {
  position: absolute;
  top: 14px;
  right: 14px;
}

.assign-panel {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
}

.assign-panel h3 {
  margin: 0 0 12px;
  font-size: 14px;
  font-weight: 700;
  color: #002458;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.assign-course-preview {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 18px;
  border-radius: 12px;
  background: #f4f8f6;
  border: 1px solid #e8eef2;
}

.assign-course-preview strong {
  display: block;
  color: #002458;
  font-size: 15px;
}

.assign-course-preview span {
  font-size: 12px;
  color: #7a8a9a;
}

.wizard-step--finish {
  display: flex;
  align-items: center;
  justify-content: center;
}

.finish-card {
  text-align: center;
  max-width: 480px;
  padding: 24px 12px;
}

.finish-card__icon {
  width: 64px;
  height: 64px;
  margin: 0 auto 16px;
  border-radius: 50%;
  background: rgba(123, 200, 163, 0.25);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #002458;
}

.finish-card h3 {
  margin: 0 0 8px;
  font-size: 22px;
  color: #002458;
}

.finish-card p {
  margin: 0 0 24px;
  color: #5a6a7a;
  line-height: 1.5;
}

.finish-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  justify-content: center;
}

@media (max-width: 960px) {
  .wizard-hero {
    flex-direction: column;
    align-items: flex-start;
    padding: 22px;
  }

  .feature-grid,
  .course-grid,
  .assign-panel {
    grid-template-columns: 1fr;
  }

  .wizard-panel__header,
  .wizard-step,
  .wizard-panel__footer {
    padding-left: 20px;
    padding-right: 20px;
  }
}
</style>
